import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';
import { useLang } from '../context/LangContext';
import './AssistantWidget.css';

function ChatIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function BotIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 8V4H8" />
      <rect width="16" height="12" x="4" y="8" rx="2" />
      <path d="M2 14h2" />
      <path d="M20 14h2" />
      <path d="M15 13v2" />
      <path d="M9 13v2" />
    </svg>
  );
}

function CloseIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function SendIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  );
}

const MAX_HISTORY = 20;

export default function AssistantWidget() {
  const { lang } = useLang();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const welcomeText =
    lang === 'en'
      ? "Hi! I'm your ReplyTek Assistant. I can help you set up your WhatsApp bot, configure your AI, manage appointments, and more. What do you need help with?"
      : '¡Hola! Soy tu Asistente ReplyTek. Puedo ayudarte a configurar tu bot de WhatsApp, tu IA, gestionar citas y más. ¿En qué puedo ayudarte?';

  const title = lang === 'en' ? 'ReplyTek Assistant' : 'Asistente ReplyTek';
  const placeholder = lang === 'en' ? 'Ask me anything...' : 'Pregúntame lo que sea...';
  const errText =
    lang === 'en'
      ? "Sorry, I couldn't reach the assistant. Please try again in a moment."
      : 'No pude conectar con el asistente. Intenta de nuevo en un momento.';

  const scrollToBottom = useCallback(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  useEffect(() => {
    if (!isOpen) return undefined;

    setMessages((prev) => {
      if (prev.length > 0) return prev;
      return [{ role: 'assistant', content: welcomeText }];
    });

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/assistant/suggestions');
        if (cancelled || !res.data?.success) return;
        const list = res.data?.data?.suggestions;
        if (Array.isArray(list)) setSuggestions(list);
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, welcomeText]);

  const buildHistoryForApi = useCallback((currentMessages) => {
    return currentMessages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .slice(-MAX_HISTORY)
      .map(({ role, content }) => ({ role, content }));
  }, []);

  const sendMessage = async (rawText) => {
    const trimmed = String(rawText || '').trim();
    if (!trimmed || isLoading) return;

    const historyForApi = buildHistoryForApi(messages);

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setShowSuggestions(false);
    setInputValue('');
    setIsLoading(true);

    try {
      const res = await api.post('/assistant/chat', {
        message: trimmed,
        conversation_history: historyForApi,
      });
      const reply = res.data?.data?.reply;
      if (res.data?.success && reply != null && String(reply).trim() !== '') {
        setMessages((prev) => [...prev, { role: 'assistant', content: String(reply) }]);
      } else {
        setMessages((prev) => [...prev, { role: 'assistant', content: errText }]);
      }
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: errText }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChipClick = (s) => {
    const primary = lang === 'en' ? s.text_en : s.text_es;
    let text = primary || s.text_en || '';
    if (s.action_page) {
      const extra =
        lang === 'en' ? ` Take me to ${s.action_page}` : ` Quiero ir a ${s.action_page}`;
      text = `${text}.${extra}`;
    }
    sendMessage(text);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          className="assistant-widget__fab"
          onClick={() => setIsOpen(true)}
          aria-label={title}
        >
          <ChatIcon size={26} />
        </button>
      )}

      <div
        className={`assistant-widget__panel ${isOpen ? 'assistant-widget__panel--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!isOpen}
        aria-label={title}
      >
        <header className="assistant-widget__header">
          <BotIcon size={22} />
          <span className="assistant-widget__header-title">{title}</span>
          <button type="button" className="assistant-widget__close" onClick={() => setIsOpen(false)} aria-label={lang === 'en' ? 'Close' : 'Cerrar'}>
            <CloseIcon />
          </button>
        </header>

        <div className="assistant-widget__messages">
          {messages.map((m, i) => (
            <div key={`${i}-${m.role}-${String(m.content).slice(0, 12)}`} className={`assistant-widget__msg-row ${m.role === 'user' ? 'assistant-widget__msg-row--user' : 'assistant-widget__msg-row--assistant'}`}>
              <div className={`assistant-widget__bubble ${m.role === 'user' ? 'assistant-widget__bubble--user' : 'assistant-widget__bubble--assistant'}`}>{m.content}</div>
            </div>
          ))}

          {showSuggestions &&
            suggestions.length > 0 &&
            messages.length > 0 &&
            messages.filter((x) => x.role === 'user').length === 0 && (
            <div className="assistant-widget__chips">
              {suggestions.map((s, idx) => (
                <button key={`${idx}-${s.action_page || idx}`} type="button" className="assistant-widget__chip" onClick={() => handleChipClick(s)}>
                  {lang === 'en' ? s.text_en : s.text_es}
                </button>
              ))}
            </div>
          )}

          {isLoading && (
            <div className="assistant-widget__msg-row assistant-widget__msg-row--assistant">
              <div className="assistant-widget__typing" aria-live="polite">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <footer className="assistant-widget__footer">
          <form className="assistant-widget__input-row" onSubmit={handleSubmit}>
            <textarea
              ref={textareaRef}
              className="assistant-widget__textarea"
              rows={1}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              disabled={isLoading}
              aria-label={placeholder}
            />
            <button type="submit" className="assistant-widget__send" disabled={isLoading || !inputValue.trim()} aria-label={lang === 'en' ? 'Send' : 'Enviar'}>
              <SendIcon />
            </button>
          </form>
        </footer>
      </div>
    </>
  );
}
